import React, { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import _debounce from 'lodash/debounce';

import { BaseComponentProps } from '@utils/types/baseComponents';
import { MinMaxType } from '@utils/types/searchListingsContext';

import styles from './styles.module.scss';

type Props = {
  value: MinMaxType;
  onChange: (value: MinMaxType) => void;
  placeholder?: string;
  type: 'price' | 'rooms';
  inputStyle?: string;
  withDebounce?: boolean;
  suggestedValues?: number[];
} & BaseComponentProps;

export function MinMaxInputs(props: Props) {
  const {
    containerStyle,
    value,
    onChange,
    inputStyle,
    placeholder,
    type,
    withDebounce = false,
    suggestedValues = [],
  } = props;

  const [focusedInput, setFocusedInput] = useState<'min' | 'max' | null>(null);
  const minInputRef = useRef<HTMLInputElement>(null);
  const maxInputRef = useRef<HTMLInputElement>(null);

  const onInputChange = (value: MinMaxType) => {
    onChange(value as unknown as MinMaxType);
  };

  const debouncedInputChange = useMemo(() => _debounce(onInputChange, 300), []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputWrapper = e.target.parentElement;
    if (e.target.value) {
      inputWrapper?.classList.add(styles.hasValue);
    } else {
      inputWrapper?.classList.remove(styles.hasValue);
    }
  };

  const handleFocus = (inputType: 'min' | 'max') => {
    if (suggestedValues.length > 0) {
      setFocusedInput(inputType);
    }
  };

  const handleSuggestionClick = (suggestion: number, type: 'min' | 'max') => {
    const newValue = { ...value, [type]: suggestion };
    onChange(newValue);
    setFocusedInput(null);

    if (minInputRef.current && maxInputRef.current) {
      if (type === 'min') {
        minInputRef.current.value = suggestion.toString();
      } else {
        maxInputRef.current.value = suggestion.toString();
      }
    }
  };

  return (
    <div className={clsx([styles.minMaxSelectors, containerStyle])}>
      <div
        className={clsx([
          styles.minMaxSelectors__inputWrapper,
          type === 'price' && value?.min && styles.hasValue,
        ])}
      >
        <input
          type="number"
          defaultValue={value?.min}
          onInput={type === 'price' ? handleInput : undefined}
          onChange={(e) =>
            (withDebounce ? debouncedInputChange : onInputChange)({
              ...value,
              min: parseInt(e.target.value) || '',
            })
          }
          onFocus={() => handleFocus('min')}
          className={clsx([styles.minMaxSelectors__input, inputStyle])}
          placeholder={placeholder || 'No min'}
          ref={minInputRef}
        />

        {focusedInput === 'min' && (
          <ul className={styles.minMaxSelectors__suggestions}>
            {suggestedValues.map((suggestion) => (
              <li
                key={suggestion}
                onClick={() => handleSuggestionClick(suggestion, 'min')}
              >
                {type === 'price' ? `$${suggestion}` : suggestion}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles.minMaxSelectors__dash} />

      <div
        className={clsx([
          styles.minMaxSelectors__inputWrapper,
          type === 'price' && value?.max && styles.hasValue,
        ])}
      >
        <input
          type="number"
          defaultValue={value?.max}
          onInput={type === 'price' ? handleInput : undefined}
          onChange={(e) =>
            (withDebounce ? debouncedInputChange : onInputChange)({
              ...value,
              max: parseInt(e.target.value) || '',
            })
          }
          onFocus={() => handleFocus('max')}
          className={clsx([styles.minMaxSelectors__input, inputStyle])}
          placeholder={placeholder || 'No max'}
          ref={maxInputRef}
        />

        {focusedInput === 'max' && (
          <ul className={styles.minMaxSelectors__suggestions}>
            {suggestedValues.map((suggestion) => (
              <li
                key={suggestion}
                onClick={() => handleSuggestionClick(suggestion, 'max')}
              >
                {type === 'price' ? `$${suggestion}` : suggestion}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
